/**
 * Thin Voyado Elevate Storefront API client.
 *
 * This file:
 * - Builds Storefront API URLs.
 * - Adds market, locale, touchpoint and Voyado session parameters.
 * - Sends query and notification requests.
 *
 * It does not render HTML, inject CSS, or transform Voyado responses.
 */
(function initializeVoyadoElevateClient(window) {
    'use strict';

    if (!window.VOYADO_CONTEXT?.enabled || window.VOYADO_ELEVATE) {
        return;
    }

    const API_BASE_PATH = '/api/storefront/v3';
    const DEFAULT_TIMEOUT = 15000;

    class VoyadoRequestError extends Error {
        constructor(message, details = {}) {
            super(message);
            this.name = 'VoyadoRequestError';
            this.status = details.status ?? null;
            this.url = details.url ?? null;
            this.response = details.response ?? null;
        }
    }

    function getContext() {
        return window.VOYADO_CONTEXT ?? {};
    }

    function notificationsEnabled() {
        return getContext().notifications?.enabled === true;
    }

    function debugLog(message, details) {
        if (getContext().debug === true) {
            console.log(`[Voyado] ${message}`, details ?? '');
        }
    }

    function debugUrl(url) {
        const redactedUrl = new URL(url);

        for (const key of ['customerKey', 'sessionKey']) {
            if (redactedUrl.searchParams.has(key)) {
                redactedUrl.searchParams.set(key, '[redacted]');
            }
        }

        return redactedUrl.toString();
    }

    function getRequiredString(value, name) {
        const normalized = String(value ?? '').trim();

        if (!normalized) {
            throw new VoyadoRequestError(`${name} is required.`);
        }

        return normalized;
    }

    function normalizeVariantKey(value, source) {
        const key = String(value ?? '').trim();
        return source === 'sku' ? key.replace(/\|/g, '_') : key;
    }

    function getConfig(overrides = {}) {
        const context = getContext();

        return Object.freeze({
            clusterId: getRequiredString(
                overrides.clusterId ?? context.clusterId,
                'VOYADO_CONTEXT.clusterId'
            ),
            market: getRequiredString(
                overrides.market ?? context.market,
                'VOYADO_CONTEXT.market'
            ),
            locale: getRequiredString(
                overrides.locale ?? context.locale,
                'VOYADO_CONTEXT.locale'
            ),
            timeout: Math.max(
                1000,
                Number(overrides.timeout ?? context.timeout ?? DEFAULT_TIMEOUT) ||
                DEFAULT_TIMEOUT
            ),
        });
    }

    function getTouchpoint() {
        return window.matchMedia?.('(max-width: 749px)').matches
            ? 'MOBILE'
            : 'DESKTOP';
    }

    /**
     * Returns a valid Elevate identity context.
     *
     * Anonymous requests contain only userContext=ANONYMOUS.
     * Personalized requests contain customerKey and sessionKey.
     */
    async function getSessionContext() {
        const session = window.VOYADO_SESSION;

        if (!session) {
            return Object.freeze({
                userContext: 'ANONYMOUS',
            });
        }

        const requestContext = await session.getRequestContext?.();

        if (requestContext?.userContext) {
            return Object.freeze({
                userContext: String(requestContext.userContext).toUpperCase(),
                ...(requestContext.customerKey
                    ? { customerKey: String(requestContext.customerKey) }
                    : {}),
                ...(requestContext.sessionKey
                    ? { sessionKey: String(requestContext.sessionKey) }
                    : {}),
            });
        }

        if (requestContext?.customerKey && requestContext?.sessionKey) {
            return Object.freeze({
                customerKey: String(requestContext.customerKey),
                sessionKey: String(requestContext.sessionKey),
            });
        }

        const userContext = await session.getUserContext?.();

        if (userContext) {
            return Object.freeze({
                userContext: String(userContext).toUpperCase(),
            });
        }

        const customerKey = await session.getCustomerKey?.();
        const sessionKey = await session.getSessionKey?.();

        if (customerKey && sessionKey) {
            return Object.freeze({
                customerKey: String(customerKey),
                sessionKey: String(sessionKey),
            });
        }

        return Object.freeze({
            userContext: 'ANONYMOUS',
        });
    }

    function applyParameters(url, parameters = {}) {
        for (const [key, value] of Object.entries(parameters)) {
            if (value === undefined || value === null || value === '') {
                continue;
            }

            if (Array.isArray(value)) {
                if (value.length) {
                    url.searchParams.set(key, value.join('|'));
                }

                continue;
            }

            url.searchParams.set(key, String(value));
        }
    }

    async function applySession(url, sessionOverride) {
        const session = sessionOverride ?? await getSessionContext();

        url.searchParams.delete('customerKey');
        url.searchParams.delete('sessionKey');
        url.searchParams.delete('userContext');

        if (session.userContext) {
            url.searchParams.set('userContext', session.userContext);
        }

        if (session.customerKey && session.sessionKey) {
            url.searchParams.set('customerKey', session.customerKey);
            url.searchParams.set('sessionKey', session.sessionKey);
        } else if (
            session.sessionKey &&
            ['UNPERSONALIZED', 'SYNTHETIC'].includes(session.userContext)
        ) {
            url.searchParams.set('sessionKey', session.sessionKey);
        }
    }

    async function readErrorResponse(response) {
        const contentType = response.headers.get('content-type') ?? '';

        try {
            return contentType.includes('application/json')
                ? await response.json()
                : await response.text();
        } catch {
            return null;
        }
    }

    class VoyadoElevateClient {
        #config;

        constructor(config = {}) {
            this.#config = getConfig(config);
        }

        get config() {
            return this.#config;
        }

        getSessionContext() {
            return getSessionContext();
        }

        searchPage(parameters, pageConfiguration = {}, options = {}) {
            return this.#post(
                '/queries/search-page',
                parameters,
                pageConfiguration,
                options
            );
        }

        landingPage(parameters, pageConfiguration = {}, options = {}) {
            return this.#post(
                '/queries/landing-page',
                parameters,
                pageConfiguration,
                options
            );
        }

        contentSearchPage(parameters, pageConfiguration = {}, options = {}) {
            return this.#post(
                '/queries/content-search-page',
                parameters,
                pageConfiguration,
                options
            );
        }

        autocomplete(parameters = {}, pageConfiguration = {}, options = {}) {
            return this.#post(
                '/queries/autocomplete',
                parameters,
                pageConfiguration,
                options
            );
        }

        productPage(parameters, pageConfiguration = {}, options = {}) {
            return this.#post(
                '/queries/product-page',
                parameters,
                pageConfiguration,
                options
            );
        }

        addToCartPopup(parameters, pageConfiguration = {}, options = {}) {
            return this.#post(
                '/queries/add-to-cart-popup',
                parameters,
                pageConfiguration,
                options
            );
        }

        cartPage(parameters, pageConfiguration = {}, options = {}) {
            return this.#post(
                '/queries/cart-page',
                parameters,
                pageConfiguration,
                options
            );
        }

        click(ticket, options = {}) {
            return this.#notify('/notifications/click', ticket, options);
        }

        addToCart(ticket, options = {}) {
            return this.#notify('/notifications/add-to-cart', ticket, options);
        }

        async end(requestContext, options = {}) {
            const sessionContext = Object.freeze({
                customerKey: getRequiredString(
                    requestContext?.customerKey,
                    'customerKey'
                ),
                sessionKey: getRequiredString(
                    requestContext?.sessionKey,
                    'sessionKey'
                ),
            });

            await this.#post(
                '/notifications/end',
                {},
                null,
                {
                    ...options,
                    keepalive: true,
                    expectsJson: false,
                    sessionContext,
                }
            );

            return true;
        }

        async #notify(path, ticket, options) {
            if (!notificationsEnabled()) {
                debugLog('Notification skipped: notifications are disabled', { path });
                return false;
            }

            const normalizedTicket = String(ticket ?? '').trim();

            if (!normalizedTicket) {
                debugLog('Notification skipped: ticket is missing', { path });
                return false;
            }

            await this.#post(
                path,
                {},
                { ticket: normalizedTicket },
                {
                    ...options,
                    keepalive: true,
                    expectsJson: false,
                }
            );

            return true;
        }

        async #post(path, parameters, body, options) {
            const url = new URL(
                `https://${this.#config.clusterId}.elevate-api.cloud${API_BASE_PATH}${path}`
            );

            applyParameters(url, {
                market: this.#config.market,
                locale: path.startsWith('/queries/')
                    ? this.#config.locale
                    : null,
                touchpoint: path.startsWith('/queries/')
                    ? getTouchpoint()
                    : null,
                ...parameters,
            });

            await applySession(url, options.sessionContext);

            const requestStartedAt = performance.now();
            const safeUrl = debugUrl(url);

            debugLog('Request', {
                method: 'POST',
                path,
                url: safeUrl,
                body,
            });

            const controller = new AbortController();
            const externalSignal = options.signal;
            const timeoutId = window.setTimeout(
                () => controller.abort(),
                this.#config.timeout
            );

            const abortRequest = () => controller.abort();

            if (externalSignal) {
                if (externalSignal.aborted) {
                    controller.abort();
                } else {
                    externalSignal.addEventListener('abort', abortRequest, {
                        once: true,
                    });
                }
            }

            try {
                const response = await fetch(url.toString(), {
                    method: 'POST',
                    headers: body === null
                        ? { Accept: 'application/json' }
                        : {
                            Accept: 'application/json',
                            'Content-Type': 'application/json;charset=utf-8',
                        },
                    body: body === null
                        ? undefined
                        : JSON.stringify(body ?? {}),
                    keepalive: options.keepalive === true,
                    signal: controller.signal,
                });

                debugLog('Response', {
                    path,
                    url: safeUrl,
                    status: response.status,
                    durationMs: Math.round(performance.now() - requestStartedAt),
                });

                if (!response.ok) {
                    throw new VoyadoRequestError(
                        `Voyado request failed with status ${response.status}.`,
                        {
                            status: response.status,
                            url: safeUrl,
                            response: await readErrorResponse(response),
                        }
                    );
                }

                if (options.expectsJson === false || response.status === 204) {
                    return null;
                }

                return await response.json();
            } catch (error) {
                debugLog('Request failed', {
                    path,
                    url: safeUrl,
                    status: error?.status ?? null,
                    message: error?.message ?? String(error),
                    durationMs: Math.round(performance.now() - requestStartedAt),
                });

                if (error instanceof VoyadoRequestError) {
                    throw error;
                }

                if (controller.signal.aborted) {
                    throw new VoyadoRequestError(
                        'Voyado request was aborted or timed out.',
                        {
                            url: safeUrl,
                        }
                    );
                }

                throw new VoyadoRequestError(
                    error instanceof Error
                        ? error.message
                        : 'Unknown Voyado request error.',
                    {
                        url: safeUrl,
                        response: error,
                    }
                );
            } finally {
                window.clearTimeout(timeoutId);
                externalSignal?.removeEventListener('abort', abortRequest);
            }
        }
    }

    window.VOYADO_ELEVATE = Object.freeze({
        createClient(config) {
            return new VoyadoElevateClient(config);
        },
        normalizeVariantKey,
        RequestError: VoyadoRequestError,
        debug: debugLog,
    });
})(window);
